import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  CheckCircle, XCircle, Clock, AlertTriangle,
  IndianRupee, RefreshCw, ChevronLeft, ChevronRight,
  ImageIcon, X
} from 'lucide-react';
import {
  adminPaymentAPI,
  AdminPaymentRecord,
  PaymentStatus,
  PaginationMeta,
  GetAllPaymentsParams,
} from '../../services/subscriptionApi';

type StatusFilter = 'all' | PaymentStatus;
type SortBy = 'submittedAt' | 'amount';

interface ConfirmDialog {
  type: 'approve' | 'reject';
  payment: AdminPaymentRecord;
  rejectionReason?: string;
}


function getStatusIcon(status: PaymentStatus) {
  switch (status) {
    case 'pending': return <Clock className="h-3.5 w-3.5" />;
    case 'approved': return <CheckCircle className="h-3.5 w-3.5" />;
    case 'rejected': return <XCircle className="h-3.5 w-3.5" />;
    default: return <AlertTriangle className="h-3.5 w-3.5" />;
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

interface AdminPaymentPanelProps { adminId: number; }

const AdminPaymentPanel = ({ adminId }: AdminPaymentPanelProps) => {
  const [payments, setPayments] = useState<AdminPaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('submittedAt');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialog | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [proofModal, setProofModal] = useState<string | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchPayments = useCallback(async (page: number, filter: StatusFilter, sort: SortBy) => {
    try {
      setLoading(true);
      setError(null);
      const params: GetAllPaymentsParams = {
        page, limit: pagination.limit,
        sortBy: sort === 'submittedAt' ? 'createdAt' : 'amount',
        order: 'DESC',
      };
      if (filter !== 'all') params.status = filter;
      const response = await adminPaymentAPI.getAllPayments(params);
      setPayments(response.data.payments);
      setPagination(response.data.pagination);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load payments.');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchPayments(1, statusFilter, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sortBy]);

  const handleApprove = async () => {
    if (!confirm || confirm.type !== 'approve') return;
    setActionLoading(true);
    try {
      await adminPaymentAPI.approvePayment(confirm.payment.id, { adminId });
      showToast('success', `Payment approved — subscription activated for ${confirm.payment.user.name}`);
      fetchPayments(pagination.page, statusFilter, sortBy);
    } catch (err: unknown) {
      showToast('error', err instanceof Error ? err.message : 'Failed to approve payment.');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const handleReject = async () => {
    if (!confirm || confirm.type !== 'reject') return;
    setActionLoading(true);
    try {
      await adminPaymentAPI.rejectPayment(confirm.payment.id, {
        adminId,
        rejectionReason: confirm.rejectionReason || undefined,
      });
      showToast('success', `Payment rejected for ${confirm.payment.user.name}`);
      fetchPayments(pagination.page, statusFilter, sortBy);
    } catch (err: unknown) {
      showToast('error', err instanceof Error ? err.message : 'Failed to reject payment.');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  // Derived stats from current page
  const pendingCount = payments.filter(p => p.status === 'pending').length;
  const approvedCount = payments.filter(p => p.status === 'approved').length;
  const rejectedCount = payments.filter(p => p.status === 'rejected').length;
  const totalAmount = payments.filter(p => p.status === 'approved').reduce((s, p) => s + p.amount, 0);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4 md:space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Payment Requests</h1>
          <p className="text-secondary-500 text-sm mt-1">Review and process subscription payment submissions</p>
        </div>
        <button
          onClick={() => fetchPayments(pagination.page, statusFilter, sortBy)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-secondary-600 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors self-start sm:self-auto"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium border ${
          toast.type === 'success'
            ? 'bg-success-50 text-success-800 border-success-200'
            : 'bg-danger-50 text-danger-800 border-danger-200'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
          {toast.text}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Pending Review', value: pendingCount, icon: Clock, color: 'from-warning-500 to-warning-600' },
          { label: 'Approved', value: approvedCount, icon: CheckCircle, color: 'from-success-500 to-success-600' },
          { label: 'Rejected', value: rejectedCount, icon: XCircle, color: 'from-danger-500 to-danger-600' },
          { label: 'Revenue (page)', value: formatCurrency(totalAmount), icon: IndianRupee, color: 'from-primary-500 to-primary-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stats-card">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 bg-gradient-to-r ${color} rounded-xl flex items-center justify-center`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="stats-value">{value}</div>
            <div className="stats-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Status tabs */}
        <div className="flex gap-1 bg-secondary-100 p-1 rounded-lg overflow-x-auto">
          {STATUS_TABS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                statusFilter === value
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto">
          <label className="text-sm text-secondary-600 whitespace-nowrap">Sort:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="px-3 py-1.5 text-sm border border-secondary-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="submittedAt">Date</option>
            <option value="amount">Amount</option>
          </select>
          {!loading && (
            <span className="text-sm text-secondary-500 whitespace-nowrap">
              {pagination.total} total
            </span>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="text-center">
            <LoadingSpinner size="xl" />
            <p className="mt-4 text-secondary-500 text-sm">Loading payments...</p>
          </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="text-center py-12">
          <AlertTriangle className="h-10 w-10 text-danger-400 mx-auto mb-3" />
          <p className="font-medium text-secondary-900">Failed to load payments</p>
          <p className="text-sm text-secondary-500 mt-1">{error}</p>
          <Button variant="secondary" size="sm" className="mt-4"
            onClick={() => fetchPayments(pagination.page, statusFilter, sortBy)}>
            Retry
          </Button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && payments.length === 0 && (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">📋</div>
          <p className="font-medium text-secondary-700">No payment requests found</p>
          <p className="text-sm text-secondary-400 mt-1">
            {statusFilter === 'all' ? 'No submissions yet.' : `No ${statusFilter} requests.`}
          </p>
        </div>
      )}

      {/* Table / Cards */}
      {!loading && !error && payments.length > 0 && (
        <Card variant="default" padding="none">
          <CardContent className="p-0">

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary-50 border-b border-secondary-200">
                  <tr>
                    {['Order ID', 'User', 'Plan', 'Amount', 'Transaction ID', 'Proof', 'Status', 'Submitted', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-100 bg-white">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-secondary-50 transition-colors">

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono text-xs font-semibold text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded">
                          {payment.orderId || '—'}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-secondary-900">{payment.user.name}</div>
                        <div className="text-xs text-secondary-400">{payment.user.email}</div>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-secondary-700 font-medium">
                        {payment.plan.name}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {payment.amountMismatch && (
                            <span title="Amount mismatch">
                              <AlertTriangle className="h-3.5 w-3.5 text-warning-500 shrink-0" />
                            </span>
                          )}
                          <span className="font-semibold text-secondary-900">{formatCurrency(payment.amount)}</span>
                        </div>
                        {payment.amountMismatch && (
                          <div className="text-xs text-warning-600 mt-0.5">
                            Expected {formatCurrency(payment.plan.price)}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono text-xs text-secondary-600">{payment.transactionId || '—'}</span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        {payment.paymentProofUrl ? (
                          <button
                            onClick={() => setProofModal(payment.paymentProofUrl!)}
                            className="group relative"
                            title="View proof"
                          >
                            <img
                              src={payment.paymentProofUrl}
                              alt="Proof"
                              className="h-9 w-9 object-cover rounded border border-secondary-200 group-hover:opacity-75 transition-opacity"
                            />
                            <ImageIcon className="absolute inset-0 m-auto h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ) : (
                          <span className="text-xs text-secondary-300 italic">None</span>
                        )}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'pending' ? 'bg-warning-100 text-warning-700' :
                          payment.status === 'approved' ? 'bg-success-100 text-success-700' :
                          payment.status === 'rejected' ? 'bg-danger-100 text-danger-700' :
                          'bg-secondary-100 text-secondary-600'
                        }`}>
                          {getStatusIcon(payment.status)}
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-xs text-secondary-400">
                        {formatDate(payment.submittedAt)}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {payment.status === 'pending' ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setConfirm({ type: 'approve', payment })}
                              title="Approve payment"
                              className="p-1.5 text-success-600 bg-success-50 border border-success-200 rounded-lg hover:bg-success-100 transition-colors"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setConfirm({ type: 'reject', payment, rejectionReason: '' })}
                              title="Reject payment"
                              className="p-1.5 text-danger-600 bg-danger-50 border border-danger-200 rounded-lg hover:bg-danger-100 transition-colors"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-secondary-300">—</span>
                        )}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-secondary-100">
              {payments.map((payment) => (
                <div key={payment.id} className="p-4 space-y-3">
                  {/* Top row: user + status */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-secondary-900 text-sm">{payment.user.name}</div>
                      <div className="text-xs text-secondary-400">{payment.user.email}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium shrink-0 ${
                      payment.status === 'pending' ? 'bg-warning-100 text-warning-700' :
                      payment.status === 'approved' ? 'bg-success-100 text-success-700' :
                      payment.status === 'rejected' ? 'bg-danger-100 text-danger-700' :
                      'bg-secondary-100 text-secondary-600'
                    }`}>
                      {getStatusIcon(payment.status)}
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div>
                      <span className="text-secondary-400">Plan</span>
                      <div className="font-medium text-secondary-800">{payment.plan.name}</div>
                    </div>
                    <div>
                      <span className="text-secondary-400">Amount</span>
                      <div className="flex items-center gap-1">
                        {payment.amountMismatch && <AlertTriangle className="h-3 w-3 text-warning-500" />}
                        <span className="font-semibold text-secondary-900">{formatCurrency(payment.amount)}</span>
                      </div>
                      {payment.amountMismatch && (
                        <div className="text-warning-600">Expected {formatCurrency(payment.plan.price)}</div>
                      )}
                    </div>
                    <div>
                      <span className="text-secondary-400">Order ID</span>
                      <div className="font-mono font-semibold text-secondary-700">{payment.orderId || '—'}</div>
                    </div>
                    <div>
                      <span className="text-secondary-400">Transaction</span>
                      <div className="font-mono text-secondary-600 truncate">{payment.transactionId || '—'}</div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-secondary-400">Submitted</span>
                      <div className="text-secondary-600">{formatDate(payment.submittedAt)}</div>
                    </div>
                  </div>

                  {/* Bottom row: proof + actions */}
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      {payment.paymentProofUrl ? (
                        <button
                          onClick={() => setProofModal(payment.paymentProofUrl!)}
                          className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-800"
                        >
                          <img
                            src={payment.paymentProofUrl}
                            alt="Proof"
                            className="h-8 w-8 object-cover rounded border border-secondary-200"
                          />
                          <span>View proof</span>
                        </button>
                      ) : (
                        <span className="text-xs text-secondary-300 italic">No proof</span>
                      )}
                    </div>
                    {payment.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setConfirm({ type: 'approve', payment })}
                          title="Approve"
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-success-700 bg-success-50 border border-success-200 rounded-lg hover:bg-success-100 transition-colors"
                        >
                          <CheckCircle className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => setConfirm({ type: 'reject', payment, rejectionReason: '' })}
                          title="Reject"
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-danger-700 bg-danger-50 border border-danger-200 rounded-lg hover:bg-danger-100 transition-colors"
                        >
                          <XCircle className="h-3.5 w-3.5" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {!loading && !error && pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-sm text-secondary-600 order-2 sm:order-none">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchPayments(pagination.page - 1, statusFilter, sortBy)}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </button>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchPayments(pagination.page + 1, statusFilter, sortBy)}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                confirm.type === 'approve' ? 'bg-success-100' : 'bg-danger-100'
              }`}>
                {confirm.type === 'approve'
                  ? <CheckCircle className="h-5 w-5 text-success-600" />
                  : <XCircle className="h-5 w-5 text-danger-600" />}
              </div>
              <button onClick={() => setConfirm(null)} className="text-secondary-400 hover:text-secondary-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <h3 className="text-lg font-semibold text-secondary-900 mb-1">
              {confirm.type === 'approve' ? 'Approve Payment' : 'Reject Payment'}
            </h3>
            <p className="text-sm text-secondary-500 mb-4">
              {confirm.type === 'approve'
                ? `This will activate a ${confirm.payment.plan.name} subscription for ${confirm.payment.user.name}.`
                : `This will reject the payment from ${confirm.payment.user.name}.`}
            </p>

            <div className="bg-secondary-50 rounded-xl p-3 text-sm space-y-1 mb-4">
              <div className="flex justify-between">
                <span className="text-secondary-500">User</span>
                <span className="font-medium text-secondary-900">{confirm.payment.user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Plan</span>
                <span className="font-medium text-secondary-900">{confirm.payment.plan.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Amount</span>
                <span className="font-medium text-secondary-900">{formatCurrency(confirm.payment.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-500">Transaction</span>
                <span className="font-mono text-xs text-secondary-700">{confirm.payment.transactionId}</span>
              </div>
            </div>

            {confirm.type === 'reject' && (
              <textarea
                placeholder="Rejection reason (optional)"
                value={confirm.rejectionReason ?? ''}
                onChange={(e) => setConfirm({ ...confirm, rejectionReason: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none mb-4"
              />
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setConfirm(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-secondary-700 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={actionLoading}
                onClick={confirm.type === 'approve' ? handleApprove : handleReject}
                className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60 ${
                  confirm.type === 'approve'
                    ? 'bg-success-600 hover:bg-success-700'
                    : 'bg-danger-600 hover:bg-danger-700'
                }`}
              >
                {actionLoading ? 'Processing...' : confirm.type === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proof image modal */}
      {proofModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setProofModal(null)}
        >
          <div className="relative max-w-2xl max-h-[90vh] mx-4">
            <button
              onClick={() => setProofModal(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg text-secondary-600 hover:text-secondary-900"
            >
              <X className="h-4 w-4" />
            </button>
            <img
              src={proofModal}
              alt="Payment proof"
              className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPaymentPanel;
